// ── Australian NDIS Market Data (2024-25) ────────────────────────────────────

export const MARKET_STATS = {
  totalSpend:        42.3,   // $B
  participants:      661000,
  providers:         18500,
  workers:           400000,
  growth2024:        14.7,   // % YoY
  projectedSpend2030: 62.0,  // $B
  techSpendPct:      2.2,    // % of total spend on software/tech
  avgProviderRevenue: 1.28,  // $M per provider average
};

export const MARKET_GROWTH = [
  { year: '2019', spend: 13.4, participants: 298000 },
  { year: '2020', spend: 19.1, participants: 391000 },
  { year: '2021', spend: 24.1, participants: 473000 },
  { year: '2022', spend: 30.7, participants: 545000 },
  { year: '2023', spend: 36.9, participants: 610000 },
  { year: '2024', spend: 42.3, participants: 661000 },
  { year: '2025*', spend: 48.8, participants: 715000 },
  { year: '2026*', spend: 54.5, participants: 768000 },
];

export const MARKET_SEGMENTS = [
  { name: 'Core Supports',       value: 68, color: '#6366f1', detail: '$28.8B' },
  { name: 'Capacity Building',   value: 17, color: '#10b981', detail: '$7.2B' },
  { name: 'Capital Supports',    value: 15, color: '#f59e0b', detail: '$6.3B' },
];

export const STATE_DISTRIBUTION = [
  { state: 'NSW', share: 28, providers: 5180, color: '#6366f1' },
  { state: 'VIC', share: 25, providers: 4625, color: '#10b981' },
  { state: 'QLD', share: 20, providers: 3700, color: '#f59e0b' },
  { state: 'WA',  share: 12, providers: 2220, color: '#ef4444' },
  { state: 'SA',  share: 8,  providers: 1480, color: '#8b5cf6' },
  { state: 'Other',share:7,  providers: 1295, color: '#06b6d4' },
];

export const KEY_TRENDS = [
  {
    title: 'NDIS PACE Rollout',
    desc: 'New NDIS computer system replacing PACE by 2026. Providers need software with PACE API compatibility.',
    impact: 'HIGH',
    opportunity: 'Build PACE integration module before competitors',
  },
  {
    title: 'Telehealth Growth',
    desc: '67% of NDIS providers now offer or plan to offer telehealth services post-COVID. Growing demand.',
    impact: 'HIGH',
    opportunity: 'Integrate video consultation with session notes',
  },
  {
    title: 'Worker Shortage',
    desc: '70,000 additional workers needed by 2025. Smart matching and recruitment tools are critical.',
    impact: 'HIGH',
    opportunity: 'AI-powered staff recruitment and matching (partially built)',
  },
  {
    title: 'Price Guide Updates',
    desc: 'NDIS price guide updates annually in July. Manual rate updates cause billing errors across the sector.',
    impact: 'MEDIUM',
    opportunity: 'Auto-sync NDIS price guide via DSS API',
  },
  {
    title: 'Compliance Pressure',
    desc: 'NDIS Quality & Safeguards Commission increased audit frequency 40% in 2024.',
    impact: 'HIGH',
    opportunity: 'Automated compliance reporting and audit-ready exports',
  },
  {
    title: 'Allied Health Convergence',
    desc: 'NDIS increasingly funds allied health (OT, physio, speech). Providers need integrated tools.',
    impact: 'MEDIUM',
    opportunity: 'Allied health practitioner module with outcome tracking',
  },
];

// ── Feature Categories ────────────────────────────────────────────────────────
export const FEATURES = [
  { id: 'client_mgmt',    label: 'Client Management',      category: 'Core' },
  { id: 'scheduling',     label: 'Shift Scheduling',        category: 'Core' },
  { id: 'staff_mgmt',     label: 'Staff Management',        category: 'Core' },
  { id: 'ndis_billing',   label: 'NDIS Billing',            category: 'Core' },
  { id: 'service_agree',  label: 'Service Agreements',      category: 'Core' },
  { id: 'mobile',         label: 'Mobile App',              category: 'Core' },
  { id: 'gps',            label: 'GPS / Live Tracking',     category: 'Operations' },
  { id: 'incidents',      label: 'Incident Management',     category: 'Operations' },
  { id: 'documents',      label: 'Document Management',     category: 'Operations' },
  { id: 'care_plans',     label: 'Care Plans & Goals',      category: 'Operations' },
  { id: 'family_portal',  label: 'Family / Carer Portal',   category: 'Engagement' },
  { id: 'medications',    label: 'Medication Management',   category: 'Operations' },
  { id: 'financial',      label: 'Financial Reporting',     category: 'Finance' },
  { id: 'ai_features',    label: 'AI / Smart Features',     category: 'Advanced' },
  { id: 'compliance',     label: 'Compliance Tracking',     category: 'Operations' },
  { id: 'timesheets',     label: 'Timesheets & Payroll',    category: 'Finance' },
  { id: 'job_board',      label: 'Job Board / Recruitment', category: 'Advanced' },
  { id: 'proda',          label: 'PRODA Integration',       category: 'Advanced' },
  { id: 'telehealth',     label: 'Telehealth / Video',      category: 'Advanced' },
  { id: 'api',            label: 'API / Integrations',      category: 'Advanced' },
];

// Score: 1=Full, 0.5=Partial/Basic, 0=Missing
export const COMPETITORS = [
  {
    id: 'shiftcare',
    name: 'ShiftCare',
    url: 'shiftcare.com',
    pricing: '$99–$299/mo',
    founded: 2016,
    hq: 'Sydney, AUS',
    focus: 'Disability & Home Care',
    employees: '50-100',
    rating: 4.2,
    color: '#6366f1',
    description: 'Market leader for shift-based care scheduling in Australia. Strong NDIS billing but limited financial analytics and AI.',
    strengths: ['Intuitive scheduling UI', 'Strong NDIS billing', 'Good mobile app', 'PRODA integration'],
    weaknesses: ['Limited financial reporting', 'No AI/smart matching', 'Basic incident management', 'No job board'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 1, gps: 1, incidents: 0.5, documents: 1, care_plans: 0.5,
      family_portal: 0.5, medications: 0, financial: 0.5, ai_features: 0,
      compliance: 1, timesheets: 1, job_board: 0, proda: 1, telehealth: 0, api: 1,
    },
  },
  {
    id: 'lumary',
    name: 'Lumary',
    url: 'lumary.com',
    pricing: '$500–$2,000/mo',
    founded: 2018,
    hq: 'Adelaide, AUS',
    focus: 'NDIS & Aged Care (Enterprise)',
    employees: '100-200',
    rating: 4.4,
    color: '#10b981',
    description: 'Salesforce-powered enterprise platform. Feature-rich but expensive. Dominates mid-to-large providers.',
    strengths: ['Enterprise-grade', 'Salesforce ecosystem', 'PRODA integrated', 'Excellent support'],
    weaknesses: ['Very expensive', 'Complex to set up', 'Overkill for small providers', 'No AI features'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 1, gps: 0.5, incidents: 1, documents: 1, care_plans: 1,
      family_portal: 1, medications: 0.5, financial: 1, ai_features: 0.5,
      compliance: 1, timesheets: 1, job_board: 0, proda: 1, telehealth: 0, api: 1,
    },
  },
  {
    id: 'brevity',
    name: 'Brevity',
    url: 'brevitycare.com',
    pricing: '$49–$149/mo',
    founded: 2015,
    hq: 'Melbourne, AUS',
    focus: 'Budget NDIS Management',
    employees: '20-50',
    rating: 3.8,
    color: '#f59e0b',
    description: 'Budget-friendly option for small NDIS providers. Core scheduling and billing only — lacks depth.',
    strengths: ['Low cost', 'Easy to learn', 'Good billing', 'PRODA connected'],
    weaknesses: ['No GPS tracking', 'No family portal', 'Basic analytics', 'Limited compliance tools'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 0.5, gps: 0, incidents: 0.5, documents: 1, care_plans: 0.5,
      family_portal: 0, medications: 0, financial: 0.5, ai_features: 0,
      compliance: 0.5, timesheets: 1, job_board: 0, proda: 1, telehealth: 0, api: 0.5,
    },
  },
  {
    id: 'iinsight',
    name: 'iinsight',
    url: 'iinsight.net',
    pricing: '$45–$120/clinician/mo',
    founded: 2006,
    hq: 'Brisbane, AUS',
    focus: 'Allied Health & NDIS',
    employees: '20-50',
    rating: 4.0,
    color: '#ef4444',
    description: 'Specialist allied health software with strong NDIS billing. Excellent for OT, physio, speech but weaker on scheduling.',
    strengths: ['Allied health workflows', 'Strong outcome tools', 'NDIS billing', 'Telehealth built-in'],
    weaknesses: ['Poor scheduling UX', 'Expensive per-seat', 'No GPS tracking', 'Weak mobile'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 1, gps: 0, incidents: 1, documents: 1, care_plans: 1,
      family_portal: 0.5, medications: 1, financial: 1, ai_features: 0,
      compliance: 1, timesheets: 1, job_board: 0, proda: 1, telehealth: 0.5, api: 1,
    },
  },
  {
    id: 'caremaster',
    name: 'CareMaster',
    url: 'caremaster.com.au',
    pricing: '$150–$400/mo',
    founded: 2012,
    hq: 'Sydney, AUS',
    focus: 'NDIS & Aged Care',
    employees: '50-100',
    rating: 4.1,
    color: '#8b5cf6',
    description: 'Comprehensive NDIS and aged care platform. Strong compliance focus. Used by 1,200+ providers.',
    strengths: ['Comprehensive feature set', 'Compliance-first', 'Strong support', 'Good reporting'],
    weaknesses: ['Outdated UI', 'No AI features', 'Complex pricing', 'Slow performance'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 1, gps: 1, incidents: 1, documents: 1, care_plans: 1,
      family_portal: 0.5, medications: 1, financial: 1, ai_features: 0.5,
      compliance: 1, timesheets: 1, job_board: 0, proda: 1, telehealth: 0, api: 1,
    },
  },
  {
    id: 'alayacare',
    name: 'AlayaCare',
    url: 'alayacare.com',
    pricing: '$1,000+/mo (enterprise)',
    founded: 2014,
    hq: 'Canada (AUS ops)',
    focus: 'Enterprise Home Health',
    employees: '500+',
    rating: 4.5,
    color: '#06b6d4',
    description: 'International enterprise platform. Most feature-complete in the market. Used by large providers (50+ staff).',
    strengths: ['Most complete feature set', 'AI & predictive analytics', 'Telehealth', 'Enterprise-grade'],
    weaknesses: ['Very expensive', 'Designed for larger orgs', 'Complex implementation', 'Less NDIS-specific'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 1, gps: 1, incidents: 1, documents: 1, care_plans: 1,
      family_portal: 1, medications: 1, financial: 1, ai_features: 1,
      compliance: 1, timesheets: 1, job_board: 0, proda: 0.5, telehealth: 1, api: 1,
    },
  },
  {
    id: 'careview',
    name: 'Careview',
    url: 'careview.com.au',
    pricing: '$50–$200/mo',
    founded: 2010,
    hq: 'Perth, AUS',
    focus: 'Community Care Records',
    employees: '20-50',
    rating: 3.7,
    color: '#ec4899',
    description: 'Focus on clinical documentation and community nursing. Limited scheduling. Strong in WA market.',
    strengths: ['Clinical documentation', 'Medication management', 'Mobile-first', 'WA community care'],
    weaknesses: ['Weak scheduling', 'No GPS', 'No financial tools', 'Niche market only'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 0.5, ndis_billing: 0.5, service_agree: 1,
      mobile: 1, gps: 0, incidents: 1, documents: 1, care_plans: 1,
      family_portal: 0, medications: 1, financial: 0.5, ai_features: 0,
      compliance: 1, timesheets: 0.5, job_board: 0, proda: 0.5, telehealth: 0.5, api: 0.5,
    },
  },
  {
    id: 'caretaskr',
    name: 'Caretaskr',
    url: 'caretaskr.com',
    pricing: '$99–$299/mo',
    founded: 2019,
    hq: 'Melbourne, AUS',
    focus: 'NDIS Operations',
    employees: '10-30',
    rating: 3.9,
    color: '#84cc16',
    description: 'Newer player targeting small-to-medium NDIS providers. Good PRODA integration. Growing fast.',
    strengths: ['Modern UI', 'PRODA integrated', 'Good scheduling', 'Competitive pricing'],
    weaknesses: ['Limited financial tools', 'No AI features', 'Small team support', 'No medications'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 1, gps: 0.5, incidents: 0.5, documents: 1, care_plans: 0.5,
      family_portal: 0.5, medications: 0, financial: 0.5, ai_features: 0,
      compliance: 1, timesheets: 1, job_board: 0, proda: 1, telehealth: 0, api: 0.5,
    },
  },
  {
    id: 'visualcare',
    name: 'VisualCare',
    url: 'visualcare.com.au',
    pricing: '$100–$350/mo',
    founded: 2008,
    hq: 'Sydney, AUS',
    focus: 'Home Care & NDIS',
    employees: '30-60',
    rating: 4.0,
    color: '#f97316',
    description: 'Established home care platform with good NDIS module. Strong financial management but dated interface.',
    strengths: ['Financial management', 'Rostering depth', 'PRODA integrated', 'Large customer base'],
    weaknesses: ['Outdated UI/UX', 'No AI features', 'Limited mobile', 'No job board'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
      mobile: 0.5, gps: 0.5, incidents: 1, documents: 1, care_plans: 1,
      family_portal: 0.5, medications: 0.5, financial: 1, ai_features: 0,
      compliance: 1, timesheets: 1, job_board: 0, proda: 1, telehealth: 0, api: 1,
    },
  },
  {
    id: 'hcpconnect',
    name: 'HCP Connect',
    url: 'hcpconnect.com.au',
    pricing: '$80–$250/mo',
    founded: 2017,
    hq: 'Adelaide, AUS',
    focus: 'Home Care Packages',
    employees: '20-40',
    rating: 3.8,
    color: '#14b8a6',
    description: 'Specialist Home Care Package software. Excellent for HCP but limited NDIS depth.',
    strengths: ['HCP financial management', 'Family portal', 'Consumer statements', 'Easy setup'],
    weaknesses: ['Limited NDIS features', 'No GPS', 'Basic scheduling', 'Niche use case'],
    scores: {
      client_mgmt: 1, scheduling: 1, staff_mgmt: 0.5, ndis_billing: 0.5, service_agree: 1,
      mobile: 1, gps: 0, incidents: 0.5, documents: 1, care_plans: 1,
      family_portal: 1, medications: 0.5, financial: 1, ai_features: 0,
      compliance: 1, timesheets: 0.5, job_board: 0, proda: 0.5, telehealth: 0.5, api: 1,
    },
  },
];

// Our software scores
export const OUR_SOFTWARE = {
  name: 'Rostery',
  scores: {
    client_mgmt: 1, scheduling: 1, staff_mgmt: 1, ndis_billing: 1, service_agree: 1,
    mobile: 1, gps: 1, incidents: 1, documents: 1, care_plans: 1,
    family_portal: 1, medications: 1, financial: 1, ai_features: 1,
    compliance: 1, timesheets: 1, job_board: 1, proda: 0, telehealth: 0, api: 0.5,
  },
};

export function getScore(scores) {
  const vals = Object.values(scores);
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100);
}

// ── Improvement Recommendations ───────────────────────────────────────────────
export const IMPROVEMENTS = [
  {
    id: 1,
    title: 'PRODA / NDIS Portal Integration',
    category: 'Integration',
    impact: 95,
    effort: 70,
    priority: 'Critical',
    color: '#ef4444',
    competitors: 7,  // out of 10 have it
    description: 'Direct integration with NDIA\'s PRODA system to submit bulk claims and receive payment confirmations automatically.',
    benefits: ['Eliminates manual claim submission', 'Reduces errors by ~85%', 'Saves 8-10 hrs/week admin', 'Matching top 7 competitors'],
    timeline: '3-4 months',
    revenue_impact: 'High — key deal-breaker for providers switching from competitors',
  },
  {
    id: 2,
    title: 'NDIS Price Guide Auto-Sync',
    category: 'Finance',
    impact: 88,
    effort: 30,
    priority: 'High',
    color: '#f59e0b',
    competitors: 3,
    description: 'Automatically sync the NDIS Support Catalogue (published July each year) into client and staff rates.',
    benefits: ['Zero billing errors from outdated rates', 'Saves hours of manual rate updates', 'Quick win vs competitors'],
    timeline: '2-3 weeks',
    revenue_impact: 'Medium — prevents billing disputes and NDIS audits',
  },
  {
    id: 3,
    title: 'Telehealth / Video Consultation',
    category: 'Clinical',
    impact: 82,
    effort: 65,
    priority: 'High',
    color: '#f59e0b',
    competitors: 2,
    description: 'Built-in video consultation with session notes, automatic billing to NDIS, and integration with care plans.',
    benefits: ['67% of providers need telehealth', 'Direct NDIS billing', 'Reduces transport costs', 'Differentiation from 8/10 competitors'],
    timeline: '2-3 months',
    revenue_impact: 'High — opens new revenue stream and client segment',
  },
  {
    id: 4,
    title: 'PACE (NDIS 2.0) Compliance Module',
    category: 'Compliance',
    impact: 90,
    effort: 75,
    priority: 'Critical',
    color: '#ef4444',
    competitors: 2,
    description: 'NDIA is rolling out PACE (new IT system) by 2026. Providers need software that is PACE API-ready before the deadline.',
    benefits: ['First-mover advantage', 'Avoid client churn to PACE-ready competitors', 'Regulatory compliance'],
    timeline: '4-6 months',
    revenue_impact: 'Critical — providers will switch to PACE-ready software',
  },
  {
    id: 5,
    title: 'Native Mobile App (iOS & Android)',
    category: 'Mobile',
    impact: 78,
    effort: 80,
    priority: 'High',
    color: '#f59e0b',
    competitors: 8,
    description: 'Publish native iOS and Android apps with offline mode, push notifications, and biometric login for staff.',
    benefits: ['Offline shift notes', 'Push notifications for shift changes', 'GPS clock-in', 'Better UX for carers'],
    timeline: '3-5 months (React Native)',
    revenue_impact: 'High — staff adoption and satisfaction increases significantly',
  },
  {
    id: 6,
    title: 'Allied Health Practitioner Module',
    category: 'Clinical',
    impact: 75,
    effort: 85,
    priority: 'Medium',
    color: '#8b5cf6',
    competitors: 2,
    description: 'Add OT, physiotherapy, speech pathology and psychology workflows with outcome measurement and NDIS billing.',
    benefits: ['New client segment (allied health)', 'NDIS funds ~$7.2B capacity building', 'Outcome tracking differentiator'],
    timeline: '4-6 months',
    revenue_impact: 'Very High — doubles addressable market',
  },
  {
    id: 7,
    title: 'Client Outcome Measurement Tools',
    category: 'Clinical',
    impact: 70,
    effort: 40,
    priority: 'Medium',
    color: '#8b5cf6',
    competitors: 3,
    description: 'Standardised outcome measurement tools (I-CAN, WHODAS, AusTOM) to track client progress against NDIS goals.',
    benefits: ['Required for NDIS Quality reviews', 'Helps justify support hours', 'Differentiator from most competitors'],
    timeline: '4-6 weeks',
    revenue_impact: 'Medium — required by NDIS Quality Safeguards Commission',
  },
  {
    id: 8,
    title: 'Staff Training & LMS Module',
    category: 'HR',
    impact: 68,
    effort: 60,
    priority: 'Medium',
    color: '#8b5cf6',
    competitors: 1,
    description: 'Built-in learning management system for mandatory NDIS training, inductions, and certification tracking.',
    benefits: ['Tracks mandatory Worker Screening compliance', 'Reduces training admin', 'Major differentiator — only 1 competitor has it'],
    timeline: '2-3 months',
    revenue_impact: 'Medium — reduces staff onboarding time by 60%',
  },
  {
    id: 9,
    title: 'Xero / MYOB Payroll Integration',
    category: 'Finance',
    impact: 72,
    effort: 35,
    priority: 'High',
    color: '#f59e0b',
    competitors: 4,
    description: 'Two-way sync with Xero and MYOB to push timesheet approvals and generate payroll runs automatically.',
    benefits: ['Eliminates double data entry', 'Auto-generates payrun from timesheets', 'Popular with accountants'],
    timeline: '3-5 weeks',
    revenue_impact: 'High — accountant referrals and reduced admin churn',
  },
  {
    id: 10,
    title: 'Calendar Sync (Google & Outlook)',
    category: 'Productivity',
    impact: 55,
    effort: 15,
    priority: 'Low',
    color: '#10b981',
    competitors: 3,
    description: 'Sync staff shifts to Google Calendar and Microsoft Outlook so carers see shifts in their preferred calendar.',
    benefits: ['Reduces shift confusion', 'Less "I didn\'t know about my shift" complaints', 'Quick win'],
    timeline: '1-2 weeks',
    revenue_impact: 'Low — but improves staff satisfaction significantly',
  },
  {
    id: 11,
    title: 'Bulk NDIS Claim Export (PACE format)',
    category: 'Finance',
    impact: 85,
    effort: 25,
    priority: 'High',
    color: '#f59e0b',
    competitors: 5,
    description: 'Generate NDIS-compliant bulk payment request files in the correct format for manual PRODA upload.',
    benefits: ['Immediate PRODA workaround', 'Reduces errors vs manual entry', 'Step 1 before full PRODA integration'],
    timeline: '1-2 weeks',
    revenue_impact: 'High — resolves the #1 billing pain point quickly',
  },
  {
    id: 12,
    title: 'Carer Wellbeing & Pulse Surveys',
    category: 'HR',
    impact: 58,
    effort: 25,
    priority: 'Low',
    color: '#10b981',
    competitors: 0,
    description: 'Weekly automated pulse surveys sent to carers to measure satisfaction, burnout risk, and flag at-risk staff.',
    benefits: ['Reduce staff turnover (avg 30% in sector)', 'No competitor offers this', 'Unique differentiator'],
    timeline: '2-3 weeks',
    revenue_impact: 'Medium — reduces $4-8k recruitment cost per staff member',
  },
];
